import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Api from "../Auth/Api";
import { decodeJWT } from "../Components/jwtUtils";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { Modal, Toast, Badge, Alert } from "react-bootstrap";
import DropdownMenu from "../Components/DropdownMenu";
import 'tippy.js/dist/tippy.css';
import Select from "react-select";
import * as XLSX from 'xlsx';
import ModalNovoAgendamento from "../Components/Agendamentos/ModalNovoAgendamento";
import ModalConfirmarAgendamento from "../Components/Agendamentos/ModalConfirmarAgendamento";
import ModalEditarAgendamento from "../Components/Agendamentos/ModalEditarAgendamento";
import ModalRealizarAgendamento from "../Components/Agendamentos/ModalRealizarAgendamento";
import ModalCancelarAgendamento from "../Components/Agendamentos/ModalCancelarAgendamento";
import ModalDetalhesAgendamento from "../Components/Agendamentos/ModalDetalhesAgendamento";
import ModalUploadListaPresenca from "../Components/Agendamentos/ModalUploadListaPresenca";
import { customStyles } from '../utils/selectStyles';
import { useAjustes } from "../Context/AjustesContext";

function AgendamentosTreinamentos() {
    const token = JSON.parse(localStorage.getItem("user_token"));
    const nivel = decodeJWT(token).nivel;
    const navigate = useNavigate();
    const { ajustes } = useAjustes();

    // Verifica se o usuário pode gerenciar agendamentos
    const podeGerenciarAgendamentos = ['Administrador', 'Gestor', 'RH'].includes(nivel);
    // Criar agendamento: Admin sempre; Gestor/RH conforme permissão por empresa.
    const podeCriarAgendamento = nivel === 'Administrador'
        || (nivel === 'Gestor' && Number(ajustes?.gestor_pode_criar_agendamento ?? 0) !== 0)
        || (nivel === 'RH' && Number(ajustes?.rh_pode_criar_agendamento ?? 0) !== 0);
    // Vê todas as unidades: Admin sempre; Gestor/RH conforme permissão por empresa.
    const veTodasUnidades = nivel === 'Administrador'
        || (nivel === 'Gestor' && Number(ajustes?.gestor_agenda_todas_unidades ?? 0) !== 0)
        || (nivel === 'RH' && Number(ajustes?.rh_agenda_todas_unidades ?? 0) !== 0);

    const [agendamentos, setAgendamentos] = useState([]);
    const [proximosAgendamentos, setProximosAgendamentos] = useState([]);
    const [dataSelecionada, setDataSelecionada] = useState(new Date());
    const [filtroMes, setFiltroMes] = useState(new Date().getMonth() + 1);
    const [filtroAno, setFiltroAno] = useState(new Date().getFullYear());
    const [filtroUnidade, setFiltroUnidade] = useState("");
    const [filtroStatus, setFiltroStatus] = useState("");
    const [unidades, setUnidades] = useState([]);
    const [diasComAgendamentos, setDiasComAgendamentos] = useState(new Set());
    const [mesAnoCalendario, setMesAnoCalendario] = useState({ mes: new Date().getMonth() + 1, ano: new Date().getFullYear() });
    const [showModalNovo, setShowModalNovo] = useState(false);
    const [showModalConfirmar, setShowModalConfirmar] = useState(false);
    const [showModalEditar, setShowModalEditar] = useState(false);
    const [showModalRealizar, setShowModalRealizar] = useState(false);
    const [showModalCancelar, setShowModalCancelar] = useState(false);
    const [showModalDetalhes, setShowModalDetalhes] = useState(false);
    const [agendamentoSelecionado, setAgendamentoSelecionado] = useState(null);
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [showModalAlerta, setShowModalAlerta] = useState(false);
    const [mensagemAlerta, setMensagemAlerta] = useState('');
    const [alertaModalSize, setAlertaModalSize] = useState('lg');
    const [reabrirModalNovo, setReabrirModalNovo] = useState(false);
    const [ordenacao, setOrdenacao] = useState({ campo: null, direcao: 'asc' });
    const [showModalConfirmados, setShowModalConfirmados] = useState(false);
    const [showModalDiarias, setShowModalDiarias] = useState(false);
    const [showModalRealizados, setShowModalRealizados] = useState(false);
    const [showModalCancelados, setShowModalCancelados] = useState(false);
    const [filtroDataEspecifica, setFiltroDataEspecifica] = useState(null);
    const [showModalUploadListaPresenca, setShowModalUploadListaPresenca] = useState(false);

    // Verificar se pode acessar
    const podeAcessar = ['Gestor', 'RH', 'Administrador'].includes(nivel);

    useEffect(() => {
        if (!podeAcessar) {
            navigate('/');
            return;
        }
    }, [nivel, navigate, podeAcessar]);

    // Carregar unidades: quem vê todas usa /api/unidades; restrito usa só as suas.
    useEffect(() => {
        async function fetchUnidades() {
            try {
                const endpoint = veTodasUnidades ? '/api/unidades' : '/api/minhas-unidades';
                const response = await Api.get(endpoint, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUnidades(response.data);
            } catch (error) {
                console.error('Erro ao carregar unidades:', error);
            }
        }
        fetchUnidades();
    }, [token, veTodasUnidades]);

    // Sincronizar filtros de mês/ano com o calendário
    useEffect(() => {
        // Quando os filtros mudarem, atualizar o calendário
        // Garantir que filtroAno seja um número válido
        const anoNumerico = typeof filtroAno === 'string' 
            ? (parseInt(filtroAno) || new Date().getFullYear())
            : (filtroAno || new Date().getFullYear());
        const mesNumerico = typeof filtroMes === 'string' 
            ? (parseInt(filtroMes) || new Date().getMonth() + 1)
            : (filtroMes || new Date().getMonth() + 1);
        
        // Verificar se os valores são válidos
        if (isNaN(anoNumerico) || anoNumerico < 2020 || anoNumerico > 2100) {
            return;
        }
        if (isNaN(mesNumerico) || mesNumerico < 1 || mesNumerico > 12) {
            return;
        }
        
        // Sempre atualizar o calendário quando os filtros mudarem
        const novaData = new Date(anoNumerico, mesNumerico - 1, 1);
        
        // Atualizar apenas se realmente mudou para evitar loops
        const dataAtual = dataSelecionada;
        const anoAtual = dataAtual.getFullYear();
        const mesAtual = dataAtual.getMonth() + 1;
        
        if (anoAtual !== anoNumerico || mesAtual !== mesNumerico) {
            setDataSelecionada(novaData);
            setMesAnoCalendario({ mes: mesNumerico, ano: anoNumerico });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroMes, filtroAno]);

    // Buscar dias com agendamentos para o calendário
    useEffect(() => {
        async function buscarDiasComAgendamentos() {
            try {
                const response = await Api.get(
                    `/api/agendamentos-treinamentos?mes=${mesAnoCalendario.mes}&ano=${mesAnoCalendario.ano}`,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );

                // Criar um Set com as datas que têm agendamentos (apenas pendentes e confirmados)
                const dias = new Set();
                response.data.forEach(ag => {
                    if (ag.status !== 'cancelado' && ag.data_treinamento) {
                        // Garantir formato YYYY-MM-DD
                        let dataFormatada = ag.data_treinamento;
                        if (dataFormatada.includes('T')) {
                            dataFormatada = dataFormatada.split('T')[0];
                        }
                        // Se vier no formato brasileiro, converter
                        if (dataFormatada.includes('/')) {
                            const [dia, mes, ano] = dataFormatada.split('/');
                            dataFormatada = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
                        }
                        dias.add(dataFormatada);
                    }
                });
                setDiasComAgendamentos(dias);
            } catch (error) {
                console.error('Erro ao buscar dias com agendamentos:', error);
            }
        }
        buscarDiasComAgendamentos();
    }, [mesAnoCalendario, token]);

    // Carregar agendamentos
    useEffect(() => {
        async function fetchAgendamentos() {
            try {
                const params = new URLSearchParams();
                if (filtroMes) params.append('mes', filtroMes);
                if (filtroAno) params.append('ano', filtroAno);
                if (filtroUnidade) params.append('unidade', filtroUnidade);
                if (filtroStatus) params.append('status', filtroStatus);

                const response = await Api.get(`/api/agendamentos-treinamentos?${params}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                // Ordenar agendamentos: pendente -> confirmado -> realizado -> cancelado
                const agendamentosOrdenados = [...response.data].sort((a, b) => {
                    const ordemStatus = {
                        'pendente': 1,
                        'confirmado': 2,
                        'realizado': 3,
                        'cancelado': 4
                    };
                    return (ordemStatus[a.status] || 99) - (ordemStatus[b.status] || 99);
                });

                setAgendamentos(agendamentosOrdenados);
            } catch (error) {
                console.error('Erro ao carregar agendamentos:', error);
            }
        }
        fetchAgendamentos();
    }, [token, filtroMes, filtroAno, filtroUnidade, filtroStatus]);

    // Carregar próximos 5 agendamentos
    useEffect(() => {
        async function fetchProximos() {
            try {
                const response = await Api.get('/api/agendamentos-treinamentos?proximos=5', {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setProximosAgendamentos(response.data);
            } catch (error) {
                console.error('Erro ao carregar próximos agendamentos:', error);
            }
        }
        fetchProximos();
    }, [token]);

    const handleDataClick = (date) => {
        setDataSelecionada(date);
        if (nivel === 'Administrador' || nivel === 'Gestor') {
            setFiltroDataEspecifica(date);
        } else if (nivel === 'RH') {
            setShowModalNovo(true);
        }
    };

    // Verificar se agendamento está vencido (data passou e não foi realizado/cancelado)
    // Regra: considerar vencido somente após 1 dia completo depois da data do treinamento
    const isAgendamentoVencido = (agendamento) => {
        if (!agendamento.data_treinamento) return false;
        if (agendamento.status === 'realizado' || agendamento.status === 'cancelado') return false;
        
        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        
        // Tratar data no formato YYYY-MM-DD sem problemas de timezone
        let dataTreinamento;
        if (typeof agendamento.data_treinamento === 'string' && agendamento.data_treinamento.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [ano, mes, dia] = agendamento.data_treinamento.split('-');
            dataTreinamento = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
        } else {
            dataTreinamento = new Date(agendamento.data_treinamento);
        }
        dataTreinamento.setHours(0, 0, 0, 0);
        
        const diffMs = hoje.getTime() - dataTreinamento.getTime();
        if (diffMs <= 0) return false; // hoje antes ou no dia do treinamento
        const diffDias = diffMs / (1000 * 60 * 60 * 24);
        
        // Só marcar como vencido depois de 1 dia completo após a data do treinamento
        return diffDias >= 1;
    };

    const getStatusBadge = (status, agendamento = null) => {
        // Se o agendamento está vencido, mostrar "Vencido" ao invés do status original
        if (agendamento && isAgendamentoVencido(agendamento)) {
            return <Badge bg="warning">Vencido</Badge>;
        }
        
        const badges = {
            pendente: <Badge bg="secondary">Pendente</Badge>,
            confirmado: <Badge bg="success">Confirmado</Badge>,
            realizado: <Badge bg="info">Realizado</Badge>,
            cancelado: <Badge bg="danger">Cancelado</Badge>
        };
        return badges[status] || <Badge bg="secondary">{status}</Badge>;
    };

    // Componente de card mobile para agendamentos
    const AgendamentoMobileCard = ({ agendamento }) => {
        return (
            <div className="col-12 mb-3 p-0">
                <div
                    className="card border-0 shadow-sm rounded-4"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                        setAgendamentoSelecionado(agendamento);
                        setShowModalDetalhes(true);
                    }}
                >
                    <div className="card-body p-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                            <div className="flex-grow-1">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span className="badge bg-secondary">ID: {agendamento.id}</span>
                                    <h6 className="card-title fw-semibold text-primary mb-0">{agendamento.nome_curso}</h6>
                                </div>
                                <div className="mb-2">
                                    {getStatusBadge(agendamento.status, agendamento)}
                                </div>
                                {(agendamento.objetivo || agendamento.conteudo_programatico) && (
                                    <div className="mb-2">
                                        {agendamento.objetivo && (
                                            <small className="text-muted d-block mb-1">
                                                <strong>Objetivo:</strong> {agendamento.objetivo}
                                            </small>
                                        )}
                                        {agendamento.conteudo_programatico && (
                                            <small className="text-muted d-block">
                                                <strong>Conteúdo Programático:</strong> {agendamento.conteudo_programatico}
                                            </small>
                                        )}
                                    </div>
                                )}
                            </div>
                            {podeGerenciarAgendamentos && (
                                <div onClick={(e) => e.stopPropagation()}>
                                    <DropdownMenu
                                        items={(() => {
                                            const items = [];
                                            
                                            if (agendamento.status === 'pendente') {
                                                if (nivel === 'Administrador') {
                                                    items.push({
                                                        icon: 'check-circle',
                                                        label: 'Confirmar',
                                                        onClick: () => {
                                                            setAgendamentoSelecionado(agendamento);
                                                            setShowModalConfirmar(true);
                                                        }
                                                    });
                                                    items.push({
                                                        icon: 'pencil',
                                                        label: 'Editar',
                                                        onClick: () => {
                                                            setAgendamentoSelecionado(agendamento);
                                                            setShowModalEditar(true);
                                                        }
                                                    });
                                                }
                                                items.push({
                                                    icon: 'x-circle',
                                                    label: 'Cancelar',
                                                    onClick: () => {
                                                        setAgendamentoSelecionado(agendamento);
                                                        setShowModalCancelar(true);
                                                    }
                                                });
                                            } else if (agendamento.status === 'confirmado') {
                                                if (new Date(agendamento.data_treinamento) <= new Date()) {
                                                    items.push({
                                                        icon: 'check2-all',
                                                        label: 'Marcar como Realizado',
                                                        onClick: () => {
                                                            setAgendamentoSelecionado(agendamento);
                                                            setShowModalRealizar(true);
                                                        }
                                                    });
                                                }
                                                if (nivel === 'Administrador') {
                                                    items.push({
                                                        icon: 'pencil',
                                                        label: 'Editar',
                                                        onClick: () => {
                                                            setAgendamentoSelecionado(agendamento);
                                                            setShowModalEditar(true);
                                                        }
                                                    });
                                                }
                                                items.push({
                                                    icon: 'x-circle',
                                                    label: 'Cancelar',
                                                    onClick: () => {
                                                        setAgendamentoSelecionado(agendamento);
                                                        setShowModalCancelar(true);
                                                    }
                                                });
                                            } else {
                                                items.push({
                                                    icon: 'info-circle',
                                                    label: 'Nenhuma ação disponível',
                                                    disabled: true
                                                });
                                            }
                                            
                                            return items;
                                        })()}
                                        align="end"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mb-2">
                            <div className="d-flex align-items-center mb-1">
                                <i className="bi bi-calendar-event me-2 text-primary"></i>
                                <small className="text-muted">
                                    <strong>Data/Hora:</strong> {formatDate(agendamento.data_treinamento)} às {formatTime(agendamento.hora_treinamento)}
                                </small>
                            </div>
                            <div className="d-flex align-items-center mb-1">
                                <i className="bi bi-building me-2 text-primary"></i>
                                <small className="text-muted">
                                    <strong>Unidade:</strong> {agendamento.id_unidade}
                                </small>
                            </div>
                            <div className="d-flex align-items-center mb-1">
                                <i className="bi bi-person me-2 text-primary"></i>
                                <small className="text-muted">
                                    <strong>Solicitante:</strong> {agendamento.nome_solicitante}
                                </small>
                            </div>
                            <div className="d-flex align-items-center">
                                <i className="bi bi-clock-history me-2 text-primary"></i>
                                <small className="text-muted">
                                    <strong>Criado em:</strong> {formatDateTime(agendamento.data_criacao)}
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        // Se for um objeto Date, converter para string primeiro
        if (dateString instanceof Date) {
            const ano = dateString.getFullYear();
            const mes = String(dateString.getMonth() + 1).padStart(2, '0');
            const dia = String(dateString.getDate()).padStart(2, '0');
            return `${dia}/${mes}/${ano}`;
        }
        // Se a data já vem no formato YYYY-MM-DD, usar diretamente sem conversão de timezone
        if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const [ano, mes, dia] = dateString.split('-');
            return `${dia}/${mes}/${ano}`;
        }
        // Caso contrário, usar a data normalmente
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
    };

    // Versão curta da data: dd/mm/aa
    const formatDateShort = (dateString) => {
        const full = formatDate(dateString); // dd/mm/aaaa
        if (!full) return '';
        const [dia, mes, ano] = full.split('/');
        return `${dia}/${mes}/${ano.slice(-2)}`;
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        return timeString.substring(0, 5);
    };

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return '';
        const date = new Date(dateTimeString);
        return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const refreshAgendamentos = () => {
        // Recarregar agendamentos após ações
        const params = new URLSearchParams();
        if (filtroMes) params.append('mes', filtroMes);
        if (filtroAno) params.append('ano', filtroAno);
        if (filtroUnidade) params.append('unidade', filtroUnidade);
        if (filtroStatus) params.append('status', filtroStatus);

        Api.get(`/api/agendamentos-treinamentos?${params}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(response => {
            // Ordenar agendamentos: pendente -> confirmado -> realizado -> cancelado
            const agendamentosOrdenados = [...response.data].sort((a, b) => {
                const ordemStatus = {
                    'pendente': 1,
                    'confirmado': 2,
                    'realizado': 3,
                    'cancelado': 4
                };
                return (ordemStatus[a.status] || 99) - (ordemStatus[b.status] || 99);
            });

            setAgendamentos(agendamentosOrdenados);
        }).catch(error => {
            console.error('Erro ao recarregar agendamentos:', error);
        });

        // Recarregar próximos 5 agendamentos
        Api.get('/api/agendamentos-treinamentos?proximos=5', {
            headers: { Authorization: `Bearer ${token}` }
        }).then(response => {
            setProximosAgendamentos(response.data);
        }).catch(error => {
            console.error('Erro ao recarregar próximos agendamentos:', error);
        });
    };

    // Opções para os selects
    const mesesOptions = [
        { value: '1', label: 'Janeiro' },
        { value: '2', label: 'Fevereiro' },
        { value: '3', label: 'Março' },
        { value: '4', label: 'Abril' },
        { value: '5', label: 'Maio' },
        { value: '6', label: 'Junho' },
        { value: '7', label: 'Julho' },
        { value: '8', label: 'Agosto' },
        { value: '9', label: 'Setembro' },
        { value: '10', label: 'Outubro' },
        { value: '11', label: 'Novembro' },
        { value: '12', label: 'Dezembro' }
    ];

    const unidadesOptions = [
        { value: '', label: 'Todas as unidades' },
        ...unidades.map(unidade => ({ value: unidade, label: unidade }))
    ];

    const statusOptions = [
        { value: '', label: 'Todos os status' },
        { value: 'pendente', label: 'Pendente' },
        { value: 'confirmado', label: 'Confirmado' },
        { value: 'realizado', label: 'Realizado' },
        { value: 'cancelado', label: 'Cancelado' }
    ];

    // Calcular estatísticas dos cards
    const calcularEstatisticas = () => {
        // Treinamentos Agendados: apenas agendamentos confirmados
        const agendados = agendamentos.filter(a => a.status === 'confirmado').length;

        // Treinamentos Realizados: permanece contando todos com status "realizado"
        const realizados = agendamentos.filter(a => a.status === 'realizado').length;

        // Treinamentos Cancelados: separar normal e com taxa
        const canceladosTodos = agendamentos.filter(a => a.status === 'cancelado');
        const canceladosComTaxa = canceladosTodos.filter(a => a.cobrar_taxa === 1 || a.cobrar_taxa === true).length;
        const canceladosSemTaxa = canceladosTodos.length - canceladosComTaxa;
        
        // Diárias Agendadas:
        // Deve considerar somente treinamentos realizados,
        // agrupando por data_treinamento + unidade (mesma data e unidade = 1 diária)
        const realizadosFiltrados = agendamentos.filter(a => a.status === 'realizado');
        const diariasSet = new Set();
        
        realizadosFiltrados.forEach(ag => {
            if (ag.data_treinamento && ag.id_unidade) {
                // Normalizar data para formato YYYY-MM-DD
                let dataFormatada = ag.data_treinamento;
                if (dataFormatada.includes('T')) {
                    dataFormatada = dataFormatada.split('T')[0];
                }
                if (dataFormatada.includes('/')) {
                    const [dia, mes, ano] = dataFormatada.split('/');
                    dataFormatada = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
                }
                // Criar chave única: data_unidade
                const chaveDiaria = `${dataFormatada}_${ag.id_unidade}`;
                diariasSet.add(chaveDiaria);
            }
        });
        
        const diarias = diariasSet.size;
        
        return { agendados, realizados, canceladosSemTaxa, canceladosComTaxa, diarias };
    };

    const estatisticas = calcularEstatisticas();

    // Função para ordenar agendamentos
    const ordenarAgendamentos = (agendamentosList) => {
        if (!ordenacao.campo) {
            // Se não há ordenação selecionada, mantém a ordenação padrão por status
            return agendamentosList;
        }

        const ordenados = [...agendamentosList].sort((a, b) => {
            let valorA, valorB;

            switch (ordenacao.campo) {
                case 'id':
                    valorA = a.id;
                    valorB = b.id;
                    break;
                case 'treinamento':
                    valorA = a.nome_curso?.toLowerCase() || '';
                    valorB = b.nome_curso?.toLowerCase() || '';
                    break;
                case 'data_hora':
                    // Combinar data e hora para ordenação
                    const dataA = new Date(`${a.data_treinamento}T${a.hora_treinamento || '00:00:00'}`);
                    const dataB = new Date(`${b.data_treinamento}T${b.hora_treinamento || '00:00:00'}`);
                    valorA = dataA.getTime();
                    valorB = dataB.getTime();
                    break;
                case 'unidade':
                    valorA = a.id_unidade?.toLowerCase() || '';
                    valorB = b.id_unidade?.toLowerCase() || '';
                    break;
                case 'status':
                    // Ordem: pendente -> confirmado -> realizado -> cancelado
                    const ordemStatus = {
                        'pendente': 1,
                        'confirmado': 2,
                        'realizado': 3,
                        'cancelado': 4
                    };
                    valorA = ordemStatus[a.status] || 99;
                    valorB = ordemStatus[b.status] || 99;
                    break;
                case 'solicitante':
                    valorA = a.nome_solicitante?.toLowerCase() || '';
                    valorB = b.nome_solicitante?.toLowerCase() || '';
                    break;
                case 'criado_em':
                    // Ordenar por data de criação
                    const dataCriacaoA = new Date(a.data_criacao || 0);
                    const dataCriacaoB = new Date(b.data_criacao || 0);
                    valorA = dataCriacaoA.getTime();
                    valorB = dataCriacaoB.getTime();
                    break;
                default:
                    return 0;
            }

            if (valorA < valorB) {
                return ordenacao.direcao === 'asc' ? -1 : 1;
            }
            if (valorA > valorB) {
                return ordenacao.direcao === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return ordenados;
    };

    // Filtrar agendamentos por data específica se admin clicou em um dia
    const agendamentosFiltrados = filtroDataEspecifica 
        ? agendamentos.filter(ag => {
            if (!ag.data_treinamento) return false;
            
            // Normalizar data do agendamento para YYYY-MM-DD
            let dataAgendamento = ag.data_treinamento;
            if (dataAgendamento.includes('T')) {
                dataAgendamento = dataAgendamento.split('T')[0];
            }
            if (dataAgendamento.includes('/')) {
                const [dia, mes, ano] = dataAgendamento.split('/');
                dataAgendamento = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
            }
            
            // Normalizar data do filtro para YYYY-MM-DD
            const anoFiltro = filtroDataEspecifica.getFullYear();
            const mesFiltro = String(filtroDataEspecifica.getMonth() + 1).padStart(2, '0');
            const diaFiltro = String(filtroDataEspecifica.getDate()).padStart(2, '0');
            const dataFiltroFormatada = `${anoFiltro}-${mesFiltro}-${diaFiltro}`;
            
            return dataAgendamento === dataFiltroFormatada;
        })
        : agendamentos;

    const agendamentosOrdenados = ordenarAgendamentos(agendamentosFiltrados);

    // Função para lidar com clique no header
    const handleOrdenacao = (campo) => {
        if (ordenacao.campo === campo) {
            // Se já está ordenando por este campo, inverte a direção
            setOrdenacao({
                campo,
                direcao: ordenacao.direcao === 'asc' ? 'desc' : 'asc'
            });
        } else {
            // Novo campo, ordena ascendente
            setOrdenacao({ campo, direcao: 'asc' });
        }
    };

    // Função para obter ícone de ordenação
    const getOrdenacaoIcon = (campo) => {
        if (ordenacao.campo !== campo) {
            return <i className="bi bi-arrow-down-up text-muted ms-2"></i>;
        }
        if (ordenacao.direcao === 'asc') {
            return <i className="bi bi-sort-up ms-2"></i>;
        }
        return <i className="bi bi-sort-down ms-2"></i>;
    };

    // Funções para preparar dados dos modais
    const getAgendamentosConfirmados = () => {
        return agendamentos.filter(a => a.status === 'confirmado').map(a => ({
            treinamento: a.nome_curso || '',
            data: formatDate(a.data_treinamento),
            hora: formatTime(a.hora_treinamento),
            unidade: a.id_unidade || ''
        }));
    };

    const getDiariasRealizadas = () => {
        const realizadosFiltrados = agendamentos.filter(a => a.status === 'realizado');
        const diariasMap = new Map();
        
        realizadosFiltrados.forEach(ag => {
            if (ag.data_treinamento && ag.id_unidade) {
                // Normalizar data para formato YYYY-MM-DD
                let dataFormatada = ag.data_treinamento;
                if (dataFormatada.includes('T')) {
                    dataFormatada = dataFormatada.split('T')[0];
                }
                if (dataFormatada.includes('/')) {
                    const [dia, mes, ano] = dataFormatada.split('/');
                    dataFormatada = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
                }
                // Criar chave única: data_unidade
                const chaveDiaria = `${dataFormatada}_${ag.id_unidade}`;
                
                if (!diariasMap.has(chaveDiaria)) {
                    // Não usar new Date('YYYY-MM-DD'): em fuso BR vira dia/mês anterior (UTC midnight).
                    const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
                    const [, mesPart] = dataFormatada.split('-');
                    const mesIndex = parseInt(mesPart, 10) - 1;

                    diariasMap.set(chaveDiaria, {
                        data: formatDate(ag.data_treinamento),
                        hora: formatTime(ag.hora_treinamento),
                        unidade: ag.id_unidade,
                        mes: meses[mesIndex] ?? meses[0]
                    });
                }
            }
        });
        
        return Array.from(diariasMap.values());
    };

    const getAgendamentosRealizados = () => {
        return agendamentos.filter(a => a.status === 'realizado').map(a => ({
            treinamento: a.nome_curso || '',
            data: formatDate(a.data_treinamento),
            hora: formatTime(a.hora_treinamento),
            unidade: a.id_unidade || ''
        }));
    };

    const getAgendamentosCancelados = () => {
        return agendamentos.filter(a => a.status === 'cancelado').map(a => ({
            treinamento: a.nome_curso || '',
            data: formatDate(a.data_treinamento),
            hora: formatTime(a.hora_treinamento),
            unidade: a.id_unidade || '',
            comTaxa: a.cobrar_taxa === 1 || a.cobrar_taxa === true
        }));
    };

    // Funções de exportação Excel
    const exportarConfirmadosExcel = () => {
        const dados = getAgendamentosConfirmados();
        const dataForExcel = dados.map(item => ({
            'Treinamento': item.treinamento,
            'Data': item.data,
            'Hora': item.hora,
            'Unidade': item.unidade
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Treinamentos Confirmados');
        
        const nomeArquivo = `treinamentos_confirmados_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, nomeArquivo);
    };

    const exportarDiariasExcel = () => {
        const dados = getDiariasRealizadas();
        const dataForExcel = dados.map(item => ({
            'Data': item.data,
            'Hora': item.hora,
            'Unidade': item.unidade,
            'Mês': item.mes
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Diárias Realizadas');
        
        const nomeArquivo = `diarias_realizadas_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, nomeArquivo);
    };

    const exportarRealizadosExcel = () => {
        const dados = getAgendamentosRealizados();
        const dataForExcel = dados.map(item => ({
            'Treinamento': item.treinamento,
            'Data': item.data,
            'Hora': item.hora,
            'Unidade': item.unidade
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Treinamentos Realizados');
        
        const nomeArquivo = `treinamentos_realizados_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, nomeArquivo);
    };

    const exportarCanceladosExcel = () => {
        const dados = getAgendamentosCancelados();
        const dataForExcel = dados.map(item => {
            const base = {
                'Treinamento': item.treinamento,
                'Data': item.data,
                'Hora': item.hora,
                'Unidade': item.unidade
            };
            if (nivel === 'Administrador') {
                base['Tipo'] = item.comTaxa ? 'Com Taxa' : 'Normal';
            }
            return base;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataForExcel);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Treinamentos Cancelados');
        
        const nomeArquivo = `treinamentos_cancelados_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, nomeArquivo);
    };

    if (!podeAcessar) {
        return null;
    }

    return (
        <>
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-success'}`}
                style={{ zIndex: 1060 }}
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            <div className="fade-in w-100 mt-3 mb-6 mb-md-3">
                <div className="rounded-3 mb-4">
                    <div className="mb-3 d-flex justify-content-between align-items-start">
                        <div>
                            <h1 className="text-primary fs-2 fw-bold mb-2">Agenda de Treinamentos</h1>
                            <p className="text-muted mb-0">
                                Gerencie e acompanhe os agendamentos de treinamentos do sistema.
                            </p>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                            {nivel === 'Administrador' && (
                                <button
                                    className="d-flex align-items-center justify-content-center btn btn-link text-muted p-0"
                                    onClick={() => {
                                        setShowModalUploadListaPresenca(true);
                                    }}
                                    style={{ textDecoration: 'none', fontSize: '0.875rem' }}
                                    title="Upload Lista de Presença"
                                >
                                    <i className="bi bi-cloud-upload"></i> 
                                    <span className="d-none d-md-block ms-1" style={{ fontSize: '0.875rem' }}>Lista de Presença</span>
                                </button>
                            )}
                            {podeCriarAgendamento && (
                                <button
                                    className="d-flex align-items-center justify-content-center btn btn-primary text-white fw-semibold"
                                    onClick={() => {
                                        setDataSelecionada(new Date());
                                        setShowModalNovo(true);
                                    }}
                                >
                                    <i className="bi bi-plus fs-4"></i> 
                                    <span className="d-none d-md-block ms-2">Novo Agendamento</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Barra de Filtros Compacta */}
                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body p-3">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-3">
                                <label className="form-label text-primary fw-medium small mb-1">Mês</label>
                                <Select
                                    value={mesesOptions.find(opt => opt.value === filtroMes.toString())}
                                    onChange={(selected) => setFiltroMes(selected ? parseInt(selected.value) : new Date().getMonth() + 1)}
                                    options={mesesOptions}
                                    styles={customStyles}
                                    placeholder="Mês"
                                    isClearable={false}
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label text-primary fw-medium small mb-1">Ano</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={filtroAno}
                                    style={{ height: '48px' }}
                                    onChange={(e) => {
                                        const valorTexto = e.target.value;
                                        if (valorTexto === '') {
                                            setFiltroAno(new Date().getFullYear());
                                            return;
                                        }
                                        const valor = parseInt(valorTexto);
                                        if (!isNaN(valor) && valor >= 2020 && valor <= 2100) {
                                            setFiltroAno(valor);
                                        }
                                    }}
                                    onBlur={(e) => {
                                        const valor = parseInt(e.target.value);
                                        if (isNaN(valor) || valor < 2020 || valor > 2100) {
                                            setFiltroAno(new Date().getFullYear());
                                        } else {
                                            setFiltroAno(valor);
                                        }
                                    }}
                                    min="2020"
                                    max="2100"
                                />
                            </div>
                            {unidades.length > 1 && (
                                <div className="col-md-3">
                                    <label className="form-label text-primary fw-medium small mb-1">Unidade</label>
                                    <Select
                                        value={unidadesOptions.find(opt => opt.value === filtroUnidade)}
                                        onChange={(selected) => setFiltroUnidade(selected ? selected.value : '')}
                                        options={unidadesOptions}
                                        styles={customStyles}
                                        placeholder="Todas as unidades"
                                        isClearable
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                            )}
                            <div className={unidades.length > 1 ? "col-md-4" : "col-md-7"}>
                                <label className="form-label text-primary fw-medium small mb-1">Status</label>
                                <Select
                                    value={statusOptions.find(opt => opt.value === filtroStatus)}
                                    onChange={(selected) => setFiltroStatus(selected ? selected.value : '')}
                                    options={statusOptions}
                                    styles={customStyles}
                                    placeholder="Todos os status"
                                    isClearable
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chips de Filtros Ativos */}
                {(filtroUnidade || filtroStatus || filtroDataEspecifica) && (
                    <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
                        {filtroUnidade && (
                            <Badge bg="primary" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Unidade: {filtroUnidade}
                                <i 
                                    className="bi bi-x cursor-pointer" 
                                    onClick={() => setFiltroUnidade('')}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                        {filtroStatus && (
                            <Badge bg="secondary" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Status: {statusOptions.find(s => s.value === filtroStatus)?.label}
                                <i 
                                    className="bi bi-x cursor-pointer" 
                                    onClick={() => setFiltroStatus('')}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                        {filtroDataEspecifica && (
                            <Badge bg="info" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Data: {formatDate(filtroDataEspecifica)}
                                <i 
                                    className="bi bi-x cursor-pointer" 
                                    onClick={() => setFiltroDataEspecifica(null)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                    </div>
                )}

                {/* Cards de Status */}
                <div className="row mb-4">
                    <div className="col-12 col-md-3 mb-3 mb-md-0">
                        <div 
                            className="card border-0 shadow-sm rounded-4 p-3 h-100"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setShowModalConfirmados(true)}
                        >
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="bg-primary bg-opacity-10 rounded-circle px-3 py-2">
                                        <i className="bi bi-calendar-check text-white fs-4"></i>
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="text-muted mb-1 small">Treinamentos Confirmados</h6>
                                    <h3 className="mb-0 text-primary fw-bold">{estatisticas.agendados}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-3 mb-3 mb-md-0">
                        <div 
                            className="card border-0 shadow-sm rounded-4 p-3 h-100"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setShowModalDiarias(true)}
                        >
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="bg-success bg-opacity-10 rounded-circle px-3 py-2">
                                        <i className="bi bi-calendar-day text-success fs-4"></i>
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="text-muted mb-1 small">Diárias Realizadas</h6>
                                    <h3 className="mb-0 text-success fw-bold">{estatisticas.diarias}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-3 mb-3 mb-md-0">
                        <div 
                            className="card border-0 shadow-sm rounded-4 p-3 h-100"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setShowModalRealizados(true)}
                        >
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="bg-info bg-opacity-10 rounded-circle px-3 py-2">
                                        <i className="bi bi-check-circle text-info fs-4"></i>
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="text-muted mb-1 small">Treinamentos Realizados</h6>
                                    <h3 className="mb-0 text-info fw-bold">{estatisticas.realizados}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-3 mb-3 mb-md-0">
                        <div 
                            className="card border-0 shadow-sm rounded-4 p-3 h-100"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setShowModalCancelados(true)}
                        >
                            <div className="d-flex align-items-center">
                                <div className="flex-shrink-0">
                                    <div className="bg-danger bg-opacity-10 rounded-circle px-3 py-2">
                                        <i className="bi bi-x-circle text-danger fs-4"></i>
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="text-muted mb-1 small">Treinamentos Cancelados</h6>
                                    {nivel === 'Administrador' ? (
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="text-center">
                                                <h4 className="mb-0 text-danger fw-bold">{estatisticas.canceladosSemTaxa}</h4>
                                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Normal</small>
                                            </div>
                                            <div className="border-start ps-3 text-center">
                                                <h4 className="mb-0 text-warning fw-bold">{estatisticas.canceladosComTaxa}</h4>
                                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Com Taxa</small>
                                            </div>
                                        </div>
                                    ) : (
                                        <h3 className="mb-0 text-danger fw-bold">{estatisticas.canceladosSemTaxa + estatisticas.canceladosComTaxa}</h3>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    {/* Calendário - Lado Esquerdo */}
                    <div className="col-12 col-lg-8 mb-4">
                        <div className="card border-0 shadow-sm rounded-4 p-3 h-100 d-flex flex-column" style={{ minHeight: '500px' }}>
                            <h5 className="mb-3">Calendário</h5>
                            <div className="flex-grow-1 d-flex align-items-start">
                                <Calendar
                                    onChange={(date) => {
                                        if (!date) return;
                                        setDataSelecionada(date);
                                        // Atualizar mês/ano quando navegar no calendário
                                        const novoMes = date.getMonth() + 1;
                                        const novoAno = date.getFullYear();
                                        // Atualizar estado do calendário e filtros
                                        setMesAnoCalendario({ mes: novoMes, ano: novoAno });
                                        setFiltroMes(novoMes);
                                        setFiltroAno(novoAno);
                                    }}
                                    onActiveStartDateChange={({ activeStartDate }) => {
                                        if (!activeStartDate) return;
                                        // Atualizar quando navegar para outro mês
                                        const novoMes = activeStartDate.getMonth() + 1;
                                        const novoAno = activeStartDate.getFullYear();
                                        // Atualizar estado do calendário e filtros
                                        setMesAnoCalendario({ mes: novoMes, ano: novoAno });
                                        setFiltroMes(novoMes);
                                        setFiltroAno(novoAno);
                                    }}
                                    value={dataSelecionada}
                                    activeStartDate={dataSelecionada}
                                    onClickDay={handleDataClick}
                                    locale="pt-BR"
                                    className="w-100"
                                tileContent={({ date, view }) => {
                                    // Só mostrar indicador na view de mês
                                    if (view === 'month') {
                                        const ano = date.getFullYear();
                                        const mes = String(date.getMonth() + 1).padStart(2, '0');
                                        const dia = String(date.getDate()).padStart(2, '0');
                                        const dataFormatada = `${ano}-${mes}-${dia}`;

                                        if (diasComAgendamentos.has(dataFormatada)) {
                                            return (
                                                <div className="d-flex justify-content-center align-items-center position-absolute" style={{ top: '2px', right: '2px', width: '8px', height: '8px' }}>
                                                    <span className="badge bg-primary rounded-circle d-inline-block" style={{ width: '8px', height: '8px', padding: 0 }}></span>
                                                </div>
                                            );
                                        }
                                    }
                                    return null;
                                }}
                                tileClassName={({ date, view }) => {
                                    if (view === 'month') {
                                        const ano = date.getFullYear();
                                        const mes = String(date.getMonth() + 1).padStart(2, '0');
                                        const dia = String(date.getDate()).padStart(2, '0');
                                        const dataFormatada = `${ano}-${mes}-${dia}`;
                                        return diasComAgendamentos.has(dataFormatada) ? 'has-agendamento' : '';
                                    }
                                    return '';
                                }}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Próximos Agendamentos - Lado Direito */}
                    <div className="col-12 col-lg-4 mb-4">
                        <div className="card border-0 shadow-sm rounded-4 p-3 h-100" style={{ minHeight: '500px' }}>
                            <h5 className="mb-3">Próximos Agendamentos</h5>
                            <div className="list-group" style={{ maxHeight: 'calc(580px - 100px)', overflowY: 'auto' }}>
                                {proximosAgendamentos.filter(ag => ag.status === 'pendente').length > 0 ? (
                                    proximosAgendamentos
                                        .filter(ag => ag.status === 'pendente')
                                        .map((agendamento) => (
                                            <div key={agendamento.id} className="list-group-item border-0 mb-2 rounded-3 border-bottom">
                                                <div className="flex-grow-1">
                                                    <div className="d-flex align-items-center gap-2 mb-1">
                                                        <span className="badge bg-secondary">#{agendamento.id}</span>
                                                        <h6 className="mb-0">{agendamento.nome_curso}</h6>
                                                    </div>
                                                    <small className="text-muted">{agendamento.id_unidade} - {formatDate(agendamento.data_treinamento)} às {formatTime(agendamento.hora_treinamento)}</small>
                                                </div>
                                            </div>
                                        ))
                                ) : (
                                    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 'calc(580px - 100px)' }}>
                                        <p className="text-muted text-center mb-0">Nenhum agendamento pendente</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Lista de Agendamentos */}
                <div className="row mt-4">
                    <div className="col-12">
                        <div className="card rounded-4 p-3 bg-none shadow-none border-none bg-md-white shadow-md-sm border-md-1" style={{ overflow: 'visible' }}>
                            <h5 className="mb-3">Agendamentos</h5>

                            {/* Versão Desktop - Tabela */}
                            <div className="d-none d-md-block">
                                <div className="position-relative">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th 
                                                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                    onClick={() => handleOrdenacao('treinamento')}
                                                >
                                                    <span className="d-inline-flex align-items-center gap-1">
                                                        Treinamento {getOrdenacaoIcon('treinamento')}
                                                    </span>
                                                </th>
                                                <th 
                                                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                    onClick={() => handleOrdenacao('data_hora')}
                                                >
                                                    <span className="d-inline-flex align-items-center gap-1">
                                                        Data/Hora {getOrdenacaoIcon('data_hora')}
                                                    </span>
                                                </th>
                                                <th 
                                                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                    onClick={() => handleOrdenacao('unidade')}
                                                >
                                                    <span className="d-inline-flex align-items-center gap-1">
                                                        Unidade {getOrdenacaoIcon('unidade')}
                                                    </span>
                                                </th>
                                                <th 
                                                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                    onClick={() => handleOrdenacao('solicitante')}
                                                >
                                                    <span className="d-inline-flex align-items-center gap-1">
                                                        Solicitante {getOrdenacaoIcon('solicitante')}
                                                    </span>
                                                </th>
                                                <th 
                                                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                    onClick={() => handleOrdenacao('criado_em')}
                                                >
                                                    <span className="d-inline-flex align-items-center gap-1">
                                                        Criado em {getOrdenacaoIcon('criado_em')}
                                                    </span>
                                                </th>
                                                <th 
                                                    style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                                                    onClick={() => handleOrdenacao('status')}
                                                >
                                                    <span className="d-inline-flex align-items-center gap-1">
                                                        Status {getOrdenacaoIcon('status')}
                                                    </span>
                                                </th>
                                                {podeGerenciarAgendamentos && <th>Ações</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {agendamentosOrdenados.length > 0 ? (
                                                agendamentosOrdenados.map((agendamento) => (
                                                    <tr
                                                        key={agendamento.id}
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => {
                                                            setAgendamentoSelecionado(agendamento);
                                                            setShowModalDetalhes(true);
                                                        }}
                                                    >
                                                        <td>
                                                            <div className="d-flex flex-column gap-1">
                                                                <span>{agendamento.nome_curso}</span>
                                                                {agendamento.objetivo && (
                                                                    <small className="text-muted">
                                                                        <strong>Objetivo:</strong> {agendamento.objetivo}
                                                                    </small>
                                                                )}
                                                                {agendamento.conteudo_programatico && (
                                                                    <small className="text-muted">
                                                                        <strong>Conteúdo Programático:</strong> {agendamento.conteudo_programatico}
                                                                    </small>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="text-left">
                                                            <small className="d-block">
                                                                {formatDateShort(agendamento.data_treinamento)} - {formatTime(agendamento.hora_treinamento)}
                                                            </small>
                                                          
                                                        </td>
                                                        <td>{agendamento.id_unidade}</td>
                                                        <td>{agendamento.nome_solicitante}</td>
                                                        <td className="text-left">
                                                            <small className="d-block">
                                                                {formatDateShort(agendamento.data_criacao)} - {new Date(agendamento.data_criacao).toLocaleTimeString('pt-BR', {
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}
                                                            </small>
                                                        </td>
                                                        <td>
                                                            {getStatusBadge(agendamento.status, agendamento)}
                                                        </td>
                                                        {podeGerenciarAgendamentos && (
                                                            <td onClick={(e) => e.stopPropagation()}>
                                                                <DropdownMenu
                                                                    items={(() => {
                                                                        const items = [];
                                                                        
                                                                        if (agendamento.status === 'pendente') {
                                                                            if (nivel === 'Administrador') {
                                                                                items.push({
                                                                                    icon: 'check-circle',
                                                                                    label: 'Confirmar',
                                                                                    onClick: () => {
                                                                                        setAgendamentoSelecionado(agendamento);
                                                                                        setShowModalConfirmar(true);
                                                                                    }
                                                                                });
                                                                                items.push({
                                                                                    icon: 'pencil',
                                                                                    label: 'Editar',
                                                                                    onClick: () => {
                                                                                        setAgendamentoSelecionado(agendamento);
                                                                                        setShowModalEditar(true);
                                                                                    }
                                                                                });
                                                                            }
                                                                            items.push({
                                                                                icon: 'x-circle',
                                                                                label: 'Cancelar',
                                                                                onClick: () => {
                                                                                    setAgendamentoSelecionado(agendamento);
                                                                                    setShowModalCancelar(true);
                                                                                }
                                                                            });
                                                                        } else if (agendamento.status === 'confirmado') {
                                                                            if (new Date(agendamento.data_treinamento) <= new Date()) {
                                                                                items.push({
                                                                                    icon: 'check2-all',
                                                                                    label: 'Marcar como Realizado',
                                                                                    onClick: () => {
                                                                                        setAgendamentoSelecionado(agendamento);
                                                                                        setShowModalRealizar(true);
                                                                                    }
                                                                                });
                                                                            }
                                                                            if (nivel === 'Administrador') {
                                                                                items.push({
                                                                                    icon: 'pencil',
                                                                                    label: 'Editar',
                                                                                    onClick: () => {
                                                                                        setAgendamentoSelecionado(agendamento);
                                                                                        setShowModalEditar(true);
                                                                                    }
                                                                                });
                                                                            }
                                                                            items.push({
                                                                                icon: 'x-circle',
                                                                                label: 'Cancelar',
                                                                                onClick: () => {
                                                                                    setAgendamentoSelecionado(agendamento);
                                                                                    setShowModalCancelar(true);
                                                                                }
                                                                            });
                                                                        } else {
                                                                            items.push({
                                                                                icon: 'info-circle',
                                                                                label: 'Nenhuma ação disponível',
                                                                                disabled: true
                                                                            });
                                                                        }
                                                                        
                                                                        return items;
                                                                    })()}
                                                                    align="end"
                                                                />
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={podeGerenciarAgendamentos ? 8 : 7} className="text-center text-muted">
                                                        Nenhum agendamento encontrado
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Versão Mobile - Cards */}
                            <div className="d-md-none">
                                {agendamentosOrdenados.length > 0 ? (
                                    <div className="row">
                                        {agendamentosOrdenados.map((agendamento) => (
                                            <AgendamentoMobileCard key={agendamento.id} agendamento={agendamento} />
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-center text-muted mb-0">Nenhum agendamento encontrado</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            {/* Modal de Alerta */}
            <Modal size={alertaModalSize} show={showModalAlerta} onHide={() => {
                setShowModalAlerta(false);
                // Reabrir modal de novo agendamento se necessário
                if (reabrirModalNovo) {
                    setTimeout(() => {
                        setShowModalNovo(true);
                        setReabrirModalNovo(false);
                    }, 300);
                }
            }} centered>
                <Modal.Header closeButton className="border-0">
                    <Modal.Title className="text-danger">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        Atenção
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="danger" className="mb-0">
                        {mensagemAlerta}
                    </Alert>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <button className="btn btn-primary" onClick={() => {
                        setShowModalAlerta(false);
                        // Reabrir modal de novo agendamento se necessário
                        if (reabrirModalNovo) {
                            setTimeout(() => {
                                setShowModalNovo(true);
                                setReabrirModalNovo(false);
                            }, 300);
                        }
                    }}>
                        Entendi
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Modais */}
            {podeCriarAgendamento && (
            <ModalNovoAgendamento
                size="lg"
                show={showModalNovo}
                onHide={() => setShowModalNovo(false)}
                dataSelecionada={dataSelecionada}
                onShowAlerta={(mensagem, modalSize = 'lg') => {
                    // Fechar modal de novo agendamento primeiro
                    setShowModalNovo(false);
                    // Marcar para reabrir após o alerta fechar
                    setReabrirModalNovo(true);
                    // Mostrar alerta após um pequeno delay para garantir que o modal fechou
                    setTimeout(() => {
                        setMensagemAlerta(mensagem);
                        setAlertaModalSize(modalSize);
                        setShowModalAlerta(true);
                    }, 300);
                }}
                onSuccess={() => {
                    // Definir toast primeiro
                    setToast({ header: 'Sucesso!', body: 'Agendamento criado com sucesso. Aguarde a confirmação.', type: 'toast-success' });
                    setShowToast(true);
                    // Fechar modal após um pequeno delay para garantir que o toast apareça
                    setTimeout(() => {
                        setShowModalNovo(false);
                    }, 100);
                    // Recarregar agendamentos
                    const fetchAgendamentos = async () => {
                        try {
                            const params = new URLSearchParams();
                            if (filtroMes) params.append('mes', filtroMes);
                            if (filtroAno) params.append('ano', filtroAno);
                            if (filtroUnidade) params.append('unidade', filtroUnidade);
                            if (filtroStatus) params.append('status', filtroStatus);

                            const response = await Api.get(`/api/agendamentos-treinamentos?${params}`, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                            // Ordenar agendamentos: pendente -> confirmado -> realizado -> cancelado
                            const agendamentosOrdenados = [...response.data].sort((a, b) => {
                                const ordemStatus = {
                                    'pendente': 1,
                                    'confirmado': 2,
                                    'realizado': 3,
                                    'cancelado': 4
                                };
                                return (ordemStatus[a.status] || 99) - (ordemStatus[b.status] || 99);
                            });

                            setAgendamentos(agendamentosOrdenados);

                            // Recarregar próximos 5 agendamentos
                            const fetchProximos = async () => {
                                try {
                                    const response = await Api.get('/api/agendamentos-treinamentos?proximos=5', {
                                        headers: { Authorization: `Bearer ${token}` }
                                    });
                                    setProximosAgendamentos(response.data);
                                } catch (error) {
                                    console.error('Erro ao recarregar próximos agendamentos:', error);
                                }
                            };
                            fetchProximos();

                            // Recarregar dias com agendamentos
                            const buscarDias = async () => {
                                try {
                                    const response = await Api.get(
                                        `/api/agendamentos-treinamentos?mes=${mesAnoCalendario.mes}&ano=${mesAnoCalendario.ano}`,
                                        {
                                            headers: { Authorization: `Bearer ${token}` }
                                        }
                                    );
                                    const dias = new Set();
                                    response.data.forEach(ag => {
                                        if (ag.status !== 'cancelado' && ag.data_treinamento) {
                                            let dataFormatada = ag.data_treinamento;
                                            if (dataFormatada.includes('T')) {
                                                dataFormatada = dataFormatada.split('T')[0];
                                            }
                                            if (dataFormatada.includes('/')) {
                                                const [dia, mes, ano] = dataFormatada.split('/');
                                                dataFormatada = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
                                            }
                                            dias.add(dataFormatada);
                                        }
                                    });
                                    setDiasComAgendamentos(dias);
                                } catch (error) {
                                    console.error('Erro ao recarregar dias:', error);
                                }
                            };
                            buscarDias();
                        } catch (error) {
                            console.error('Erro ao recarregar agendamentos:', error);
                        }
                    };
                    fetchAgendamentos();
                }}
            />
            )}

            {/* Modal de Confirmação - apenas Administrador */}
            {nivel === 'Administrador' && agendamentoSelecionado && (
                <ModalConfirmarAgendamento
                    show={showModalConfirmar}
                    onHide={() => {
                        setShowModalConfirmar(false);
                        setAgendamentoSelecionado(null);
                    }}
                    agendamento={agendamentoSelecionado}
                    onSuccess={() => {
                        setShowModalConfirmar(false);
                        setAgendamentoSelecionado(null);
                        setToast({ header: 'Sucesso!', body: 'Agendamento confirmado com sucesso.', type: 'toast-success' });
                        setShowToast(true);
                        refreshAgendamentos();
                    }}
                />
            )}

            {/* Modais de Gerenciamento - Administrador, Gestor e RH */}
            {podeGerenciarAgendamentos && agendamentoSelecionado && (
                <>
                    {/* Modal de Edição - Apenas Administrador */}
                    {nivel === 'Administrador' && (
                        <ModalEditarAgendamento
                            show={showModalEditar}
                            onHide={() => {
                                setShowModalEditar(false);
                                setAgendamentoSelecionado(null);
                            }}
                            agendamento={agendamentoSelecionado}
                            onSuccess={() => {
                                setShowModalEditar(false);
                                setAgendamentoSelecionado(null);
                                setToast({ header: 'Sucesso!', body: 'Agendamento editado com sucesso.', type: 'toast-success' });
                                setShowToast(true);
                                refreshAgendamentos();
                            }}
                        />
                    )}

                    <ModalRealizarAgendamento
                        show={showModalRealizar}
                        onHide={() => {
                            setShowModalRealizar(false);
                            setAgendamentoSelecionado(null);
                        }}
                        agendamento={agendamentoSelecionado}
                        onSuccess={(result = {}) => {
                            setShowModalRealizar(false);
                            setAgendamentoSelecionado(null);

                            let body = 'Agendamento marcado como realizado.';
                            if (result.parcial && result.mensagem) {
                                body = result.mensagem;
                            } else if (result.praticaRegistrada && result.totalUsuarios) {
                                body = `Agendamento realizado e prática registrada para ${result.totalUsuarios} participante(s).`;
                            }

                            setToast({
                                header: result.parcial ? 'Atenção' : 'Sucesso!',
                                body,
                                type: result.parcial ? 'toast-error' : 'toast-success',
                            });
                            setShowToast(true);
                            refreshAgendamentos();
                        }}
                    />

                    <ModalCancelarAgendamento
                        show={showModalCancelar}
                        onHide={() => {
                            setShowModalCancelar(false);
                            setAgendamentoSelecionado(null);
                        }}
                        agendamento={agendamentoSelecionado}
                        onSuccess={() => {
                            setShowModalCancelar(false);
                            setAgendamentoSelecionado(null);
                            setToast({ header: 'Sucesso!', body: 'Agendamento cancelado com sucesso.', type: 'toast-success' });
                            setShowToast(true);
                            refreshAgendamentos();
                        }}
                    />
                </>
            )}

            {/* Modal de Detalhes */}
            <ModalDetalhesAgendamento
                show={showModalDetalhes}
                onHide={() => {
                    setShowModalDetalhes(false);
                    setAgendamentoSelecionado(null);
                }}
                agendamento={agendamentoSelecionado}
                onRelatorioCriticoChange={async () => {
                    // Recarregar agendamentos após mudança no relatório crítico
                    await refreshAgendamentos();
                    
                    // Atualizar agendamento selecionado se ainda estiver aberto
                    if (agendamentoSelecionado?.id) {
                        try {
                            const params = new URLSearchParams();
                            if (filtroMes) params.append('mes', filtroMes);
                            if (filtroAno) params.append('ano', filtroAno);
                            if (filtroUnidade) params.append('unidade', filtroUnidade);
                            if (filtroStatus) params.append('status', filtroStatus);

                            const response = await Api.get(`/api/agendamentos-treinamentos?${params}`, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                            
                            // Encontrar o agendamento atualizado
                            const agendamentoAtualizado = response.data.find(a => a.id === agendamentoSelecionado.id);
                            if (agendamentoAtualizado) {
                                setAgendamentoSelecionado(agendamentoAtualizado);
                            }
                        } catch (error) {
                            console.error('Erro ao atualizar agendamento selecionado:', error);
                        }
                    }
                }}
            />

            {/* Modal Treinamentos Confirmados */}
            <Modal show={showModalConfirmados} onHide={() => setShowModalConfirmados(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Treinamentos Confirmados</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {getAgendamentosConfirmados().length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Treinamento</th>
                                        <th>Data</th>
                                        <th>Hora</th>
                                        <th>Unidade</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getAgendamentosConfirmados().map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.treinamento}</td>
                                            <td>{item.data}</td>
                                            <td>{item.hora}</td>
                                            <td>{item.unidade}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-muted text-center">Nenhum treinamento confirmado encontrado.</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={() => setShowModalConfirmados(false)}>
                        Fechar
                    </button>
                    <button className="btn btn-success" onClick={exportarConfirmadosExcel}>
                        <i className="bi bi-file-earmark-spreadsheet-fill me-2"></i>
                        Exportar Excel
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Modal Diárias Realizadas */}
            <Modal show={showModalDiarias} onHide={() => setShowModalDiarias(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Diárias Realizadas</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {getDiariasRealizadas().length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Data</th>
                                        <th>Hora</th>
                                        <th>Unidade</th>
                                        <th>Mês</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getDiariasRealizadas().map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.data}</td>
                                            <td>{item.hora}</td>
                                            <td>{item.unidade}</td>
                                            <td>{item.mes}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-muted text-center">Nenhuma diária realizada encontrada.</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={() => setShowModalDiarias(false)}>
                        Fechar
                    </button>
                    <button className="btn btn-success" onClick={exportarDiariasExcel}>
                        <i className="bi bi-file-earmark-spreadsheet-fill me-2"></i>
                        Exportar Excel
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Modal Treinamentos Realizados */}
            <Modal size="xl" show={showModalRealizados} onHide={() => setShowModalRealizados(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Treinamentos Realizados</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {getAgendamentosRealizados().length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Treinamento</th>
                                        <th>Data</th>
                                        <th>Hora</th>
                                        <th>Unidade</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getAgendamentosRealizados().map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.treinamento}</td>
                                            <td>{item.data}</td>
                                            <td>{item.hora}</td>
                                            <td>{item.unidade}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-muted text-center">Nenhum treinamento realizado encontrado.</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={() => setShowModalRealizados(false)}>
                        Fechar
                    </button>
                    <button className="btn btn-success" onClick={exportarRealizadosExcel}>
                        <i className="bi bi-file-earmark-spreadsheet-fill me-2"></i>
                        Exportar Excel
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Modal Treinamentos Cancelados */}
            <Modal size="xl" show={showModalCancelados} onHide={() => setShowModalCancelados(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Treinamentos Cancelados</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {getAgendamentosCancelados().length > 0 ? (
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Treinamento</th>
                                        <th>Data</th>
                                        <th>Hora</th>
                                        <th>Unidade</th>
                                        {nivel === 'Administrador' && <th>Tipo</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {getAgendamentosCancelados().map((item, index) => (
                                        <tr key={index}>
                                            <td>{item.treinamento}</td>
                                            <td>{item.data}</td>
                                            <td>{item.hora}</td>
                                            <td>{item.unidade}</td>
                                            {nivel === 'Administrador' && (
                                                <td>
                                                    {item.comTaxa ? (
                                                        <Badge bg="warning" text="dark">Com Taxa</Badge>
                                                    ) : (
                                                        <Badge bg="secondary">Normal</Badge>
                                                    )}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className="text-muted text-center">Nenhum treinamento cancelado encontrado.</p>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button className="btn btn-secondary" onClick={() => setShowModalCancelados(false)}>
                        Fechar
                    </button>
                    <button className="btn btn-success" onClick={exportarCanceladosExcel}>
                        <i className="bi bi-file-earmark-spreadsheet-fill me-2"></i>
                        Exportar Excel
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Modal Upload Lista de Presença */}
            <ModalUploadListaPresenca
                show={showModalUploadListaPresenca}
                onHide={() => setShowModalUploadListaPresenca(false)}
                onSuccess={() => {
                    // Recarregar agendamentos após upload bem-sucedido
                    refreshAgendamentos();
                }}
            />
        </>
    );
}

export default AgendamentosTreinamentos;

